/**
 * Every PDF operator pdf.js can emit, and what kind of thing it is.
 *
 * **This table is the whole of "nothing is missing" on the input side.** The
 * ledger's job is to prove that everything read out of a page was either turned
 * into an entity or recorded as a quality issue, and that proof is worthless
 * unless the set of things it could have read is known. An operator with no
 * classification here is counted as `unclassified` and fails the run, so a
 * pdf.js version that adds one — or one this extractor never considered —
 * becomes a loud failure rather than content that quietly disappears.
 *
 * `operator-classes.test.ts` asserts the table covers every name pdf.js
 * publishes, against the library itself rather than against a copy of this list.
 * That test is the reason the table can be trusted: it is checked against the
 * source of truth on every run, not written once from the documentation.
 */

export type OperatorClass =
  /** Changes graphics or text state, or brackets a construct. Draws nothing. */
  | 'state'
  /** Path construction and painting. Accounted for at segment granularity. */
  | 'path'
  /** Places characters on the page. */
  | 'text'
  /** Raster. Vector extraction does not read inside it. */
  | 'image'
  /** A smooth colour fill, which has no entity in the Phase 1 contract. */
  | 'shading'
  /** An external object whose content pdf.js expands inline. */
  | 'xobject';

/**
 * The classes that put marks on the page.
 *
 * `state` is the only one that legitimately produces neither an entity nor a
 * quality issue, which is what makes it the load-bearing distinction here: call
 * a drawing operator `state` and its content vanishes with the ledger still
 * reconciling.
 */
export const DRAWING_CLASSES: readonly OperatorClass[] = [
  'path',
  'text',
  'image',
  'shading',
  'xobject',
];

export const OPERATOR_CLASSES: Readonly<Record<string, OperatorClass>> = {
  // --- state: graphics state -------------------------------------------------
  dependency: 'state',
  setLineWidth: 'state',
  setLineCap: 'state',
  setLineJoin: 'state',
  setMiterLimit: 'state',
  setDash: 'state',
  setRenderingIntent: 'state',
  setFlatness: 'state',
  setGState: 'state',
  save: 'state',
  restore: 'state',
  transform: 'state',

  // --- state: colour ---------------------------------------------------------
  setStrokeColorSpace: 'state',
  setFillColorSpace: 'state',
  setStrokeColor: 'state',
  setStrokeColorN: 'state',
  setFillColor: 'state',
  setFillColorN: 'state',
  setStrokeGray: 'state',
  setFillGray: 'state',
  setStrokeRGBColor: 'state',
  setFillRGBColor: 'state',
  setStrokeCMYKColor: 'state',
  setFillCMYKColor: 'state',
  setStrokeTransparent: 'state',
  setFillTransparent: 'state',

  // --- state: text state and positioning -------------------------------------
  // `beginText` and `endText` bracket a text object and draw nothing; the
  // showing operators below are what place characters.
  beginText: 'state',
  endText: 'state',
  setCharSpacing: 'state',
  setWordSpacing: 'state',
  setHScale: 'state',
  setLeading: 'state',
  setFont: 'state',
  setTextRenderingMode: 'state',
  setTextRise: 'state',
  moveText: 'state',
  setLeadingMoveText: 'state',
  setTextMatrix: 'state',
  nextLine: 'state',
  setCharWidth: 'state',
  setCharWidthAndBounds: 'state',

  // --- state: structure ------------------------------------------------------
  markPoint: 'state',
  markPointProps: 'state',
  beginMarkedContent: 'state',
  beginMarkedContentProps: 'state',
  endMarkedContent: 'state',
  beginCompat: 'state',
  endCompat: 'state',
  paintFormXObjectBegin: 'state',
  paintFormXObjectEnd: 'state',
  beginGroup: 'state',
  endGroup: 'state',
  beginAnnotation: 'state',
  endAnnotation: 'state',

  // --- path ------------------------------------------------------------------
  moveTo: 'path',
  lineTo: 'path',
  curveTo: 'path',
  curveTo2: 'path',
  curveTo3: 'path',
  closePath: 'path',
  rectangle: 'path',
  constructPath: 'path',
  stroke: 'path',
  closeStroke: 'path',
  fill: 'path',
  eoFill: 'path',
  fillStroke: 'path',
  eoFillStroke: 'path',
  closeFillStroke: 'path',
  closeEOFillStroke: 'path',
  endPath: 'path',
  clip: 'path',
  eoClip: 'path',

  // --- text ------------------------------------------------------------------
  showText: 'text',
  showSpacedText: 'text',
  nextLineShowText: 'text',
  nextLineSetSpacingShowText: 'text',

  // --- image -----------------------------------------------------------------
  beginInlineImage: 'image',
  beginImageData: 'image',
  endInlineImage: 'image',
  paintImageMaskXObject: 'image',
  paintImageMaskXObjectGroup: 'image',
  paintImageXObject: 'image',
  paintInlineImageXObject: 'image',
  paintInlineImageXObjectGroup: 'image',
  paintImageXObjectRepeat: 'image',
  paintImageMaskXObjectRepeat: 'image',
  paintSolidColorImageMask: 'image',

  // --- shading and external objects ------------------------------------------
  shadingFill: 'shading',
  paintXObject: 'xobject',
};

export function classOf(operator: string): OperatorClass | null {
  return OPERATOR_CLASSES[operator] ?? null;
}
